import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IMG_URl } from "src/common/img_url";
import { modelFields } from "./ModelsFields";
import Visibility from "../utils/Visibility";
import { formatDate } from "../utils/FormatDate";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import UAEDirhamIcon from "../utils/UAEDirhamIcon";
import { 
  User, 
  Calendar, 
  FileText,
  CheckCircle2,
  XCircle,
  Tag,
  Package,
  Settings,
  Building,
  ShoppingCart,
} from "lucide-react";

const getLocalizedValue = (obj, baseField, lang) => {
  const fieldName = `${baseField}_${lang}`;
  return obj[fieldName] ?? obj[baseField] ?? "—";
};

const isLocalizedField = (field) =>
  ["name", "description", "title", "address"].includes(field);

const isImageField = (field) => ["image", "image_url", "photo"].includes(field);

const isBooleanField = (field) => 
  ["is_active", "is_first_register", "is_free_delivery", "new", "popular", "unavailable", "hidden", "active", "status", "refund", "is_paid"].includes(field);

const isDateField = (field) => 
  ["starts_at", "expires_at", "created_at", "updated_at", "time_takeaway"].includes(field);

const isPriceField = (field) => 
  ["price", "price_offer", "total", "final_total", "discount_value", "wallet_amount", "delivary_fees", "minimum_order_amount", "value", "wallet"].includes(field);

// الحقول المخفية لكل model
const hiddenFields = {
  Order: ["fcm_token", "ngenius_payment_data", "payment_intent_id", "id"],
  // يمكن إضافة المزيد من الحقول المخفية لـ models أخرى هنا
};

const isHiddenField = (field, model) => {
  return hiddenFields[model]?.includes(field) || false;
};

const formatFieldValue = (value, field, t) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  
  // Check if value is an object or array
  if (typeof value === "object" && !(value instanceof Date)) {
    // Return a placeholder that will be handled separately
    return null; // Will be handled in the render
  }
  
  if (isBooleanField(field)) {
    return value ? (
      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        {t("gloabl.yes_text")}
      </Badge>
    ) : (
      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
        <XCircle className="w-3 h-3 mr-1" />
        {t("gloabl.no_text")}
      </Badge>
    );
  }
  
  if (isDateField(field)) {
    try {
      return formatDate(new Date(value));
    } catch {
      return String(value);
    }
  }
  
  if (isPriceField(field)) {
    return (
      <span className="flex items-center gap-1 font-semibold">
        <UAEDirhamIcon className="w-4 h-4" />
        {Number(value).toFixed(2)}
      </span>
    );
  }
  
  return String(value);
};

const getModelIcon = (model) => {
  const icons = {
    Coupon: Tag,
    Product: Package,
    Setting: Settings,
    Branch: Building,
    Order: ShoppingCart,
    User: User,
    Customer: User,
    Category: Package,
    Section: FileText,
    Option: FileText,
    SubOption: FileText,
    Notification: FileText,
    Table: Building,
  };
  return icons[model] || FileText;
};

const LogDetailes = ({ log, language = "en" }) => {
  const { t } = useTranslation();
  const { model, user, created_at, action, details, model_id } = log;
  const fields = modelFields[model] || [];
  const [newDetails, setNewDetails] = useState(details);
  const ModelIcon = getModelIcon(model);

  useEffect(() => {
    if (log.action === "Send" && log.model === "Notification") {
      setNewDetails(log.details.notification);
    } else {
      setNewDetails(details);
    }
  }, [log, details]);

  const getActionColor = (action) => {
    switch (action) {
      case "Add":
        return "bg-green-100 text-green-800 border-green-200";
      case "Update":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Delete":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-gray-100`}>
                <ModelIcon className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <CardTitle className="text-xl">{t(`models.${model}`)}</CardTitle>
                {model_id && (
                  <p className="text-sm text-muted-foreground">
                    {t("gloabl.model")} ID: {model_id}
                  </p>
                )}
              </div>
            </div>
            <Badge className={getActionColor(action)}>
              {t(`actions.${action}`)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-500" />
              <span className="font-medium">{t("gloabl.user")}:</span>
              <span>{user || t("gloabl.no_data")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="font-medium">{t("gloabl.date")}:</span>
              <span>{formatDate(new Date(created_at))}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {t("gloabl.details")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Visibility visible={newDetails?.target}>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm">
                  <strong className="text-blue-900">{t(`gloabl.target`)}:</strong>{" "}
                  <span className="text-blue-700">{newDetails.target}</span>
                </p>
              </div>
            </Visibility>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields
                .filter(fieldKey => !isHiddenField(fieldKey, model)) // تصفية الحقول المخفية
                .map((fieldKey) => {
                let baseField = fieldKey.split("_")[0];
                let labelKey = fieldKey; // استخدام المفتاح الكامل للترجمة
                let value = null;
                let displayValue = null;

                // Special handling for total field - show as sub_total
                if (fieldKey === "total") {
                  baseField = "sub_total";
                  labelKey = "sub_total";
                }
                // For Setting model, use the full fieldKey for translation
                else if (model === "Setting") {
                  labelKey = fieldKey;
                }

                // If it's localized
                if (isLocalizedField(baseField)) {
                  value = getLocalizedValue(newDetails, baseField, language);
                  displayValue = value;
                }
                // If it's an image field
                else if (isImageField(fieldKey) && newDetails?.[fieldKey]) {
                  return (
                    <div key={fieldKey} className="md:col-span-2">
                      <p className="text-sm font-medium mb-2">
                        {t(`gloabl.${labelKey}`)}:
                      </p>
                      <div className="mt-1">
                        <img
                          src={`${IMG_URl}/${newDetails[fieldKey]}`}
                          alt={fieldKey}
                          className="w-48 h-auto rounded-lg border shadow-sm"
                        />
                      </div>
                    </div>
                  );
                }
                // Special handling for Order status
                else if (model === "Order" && fieldKey === "status") {
                  value = newDetails?.[fieldKey];
                  if (value) {
                    const getStatusColor = (status) => {
                      switch (status) {
                        case "pending":
                          return "bg-yellow-100 text-yellow-800 border-yellow-200";
                        case "processing":
                          return "bg-blue-100 text-blue-800 border-blue-200";
                        case "delivarying":
                          return "bg-purple-100 text-purple-800 border-purple-200";
                        case "completed":
                          return "bg-green-100 text-green-800 border-green-200";
                        case "canseled":
                          return "bg-red-100 text-red-800 border-red-200";
                        default:
                          return "bg-gray-100 text-gray-800 border-gray-200";
                      }
                    };
                    displayValue = (
                      <Badge className={getStatusColor(value)}>
                        {t(`orderStatus.${value}`)}
                      </Badge>
                    );
                  }
                }
                // Special handling for Order type
                else if (model === "Order" && fieldKey === "type") {
                  value = newDetails?.[fieldKey];
                  if (value) {
                    const getOrderTypeColor = (type) => {
                      switch (type) {
                        case "delivary":
                          return "bg-purple-100 text-purple-800 border-purple-200";
                        case "take_away":
                          return "bg-blue-100 text-blue-800 border-blue-200";
                        case "dine_in":
                          return "bg-green-100 text-green-800 border-green-200";
                        default:
                          return "bg-gray-100 text-gray-800 border-gray-200";
                      }
                    };
                    // Handle both "dine_in" and "dein_in" for translation key
                    const translationKey = value === "dine_in" ? "dein_in" : value;
                    displayValue = (
                      <Badge className={getOrderTypeColor(value)}>
                        {t(`orderType.${translationKey}`)}
                      </Badge>
                    );
                  }
                }
                // Special handling for Setting model - first_register_coupon_id
                else if (model === "Setting" && fieldKey === "first_register_coupon_id") {
                  value = newDetails?.[fieldKey];
                  // This will be handled by formatFieldValue, but we need to get the coupon code
                  displayValue = formatFieldValue(value, fieldKey, t);
                }
                // Default field
                else {
                  value = newDetails?.[fieldKey];
                  displayValue = formatFieldValue(value, fieldKey, t);
                }

                if (value === null || value === undefined || value === "") {
                  return null; // Skip empty fields
                }

                return (
                  <div key={fieldKey} className="p-3  rounded-lg border">
                    <p className="text-xs text-gray-500 mb-1">
                      {t(`gloabl.${labelKey}`)}
                    </p>
                    <div className="text-sm font-medium">
                      {displayValue}
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LogDetailes;
